import React, { useEffect } from 'react';
import { Siswa } from '../types/index.js';
import { Printer, X } from 'lucide-react';

interface VirtualAccountCardModalProps {
  students: Siswa[];
  selectedClass?: string;
  onClose: () => void;
}

export const VirtualAccountCardModal: React.FC<VirtualAccountCardModalProps> = ({
  students,
  selectedClass = '1 AL BAYAN',
  onClose,
}) => {
  const filtered = students.filter((s) => !selectedClass || s.kelasNama === selectedClass);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      id="va-card-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto cursor-pointer"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl max-w-4xl w-full border border-slate-200 overflow-hidden my-6 animate-in fade-in cursor-default relative"
      >
        {/* Top Control Bar */}
        <div className="bg-sky-800 text-white px-4 sm:px-6 py-3 flex items-center justify-between print:hidden">
          <span className="font-bold text-xs sm:text-sm">Preview Cetak Nomor Virtual Account Siswa</span>
          <div className="flex items-center gap-2">
            <button
              id="btn-print-va-top"
              type="button"
              onClick={() => window.print()}
              className="flex items-center gap-1.5 bg-sky-600 hover:bg-sky-500 active:bg-sky-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition cursor-pointer shadow-xs"
            >
              <Printer className="w-4 h-4" />
              <span className="hidden xs:inline">Cetak Halaman (Print)</span>
            </button>
            <button
              id="btn-close-va-top"
              type="button"
              onClick={onClose}
              title="Tutup (Esc)"
              aria-label="Tutup dialog"
              className="flex items-center gap-1 bg-white/15 hover:bg-rose-600 active:bg-rose-700 text-white px-2.5 py-1.5 rounded-lg transition text-xs font-bold cursor-pointer border border-white/20"
            >
              <X className="w-4 h-4" />
              <span>Tutup</span>
            </button>
          </div>
        </div>

        {/* Replicating Page 3 Document Exact Layout */}
        <div className="p-5 sm:p-8 text-slate-800 bg-white" id="printable-va-document">
          {/* Header with SDMT Logo & Text */}
          <div className="flex items-center justify-center gap-4 mb-6 border-b pb-4">
            <div className="w-14 h-14 rounded-full bg-sky-900 text-amber-400 font-extrabold flex items-center justify-center text-xl border-2 border-amber-400 shrink-0">
              SDMT
            </div>
            <div className="text-center">
              <h1 className="text-base sm:text-lg font-extrabold tracking-tight text-slate-900 uppercase">
                SD MUHAMMADIYAH TERPADU PONOROGO
              </h1>
              <h2 className="text-xs sm:text-sm font-bold text-sky-800 uppercase tracking-wide">
                NOMOR VIRTUAL ACCOUNT SISWA
              </h2>
            </div>
          </div>

          {/* Subheader */}
          <div className="mb-3 font-bold text-xs uppercase text-slate-800">
            KELAS: {selectedClass || 'SEMUA KELAS'}
          </div>

          {/* Table Exact from page 3 */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs border border-slate-300">
              <thead>
                <tr className="bg-slate-100 text-slate-800 font-bold border-b border-slate-300">
                  <th className="py-2 px-2 text-center w-12 border-r border-slate-300">NO</th>
                  <th className="py-2 px-3 text-center w-24 border-r border-slate-300">NIS</th>
                  <th className="py-2 px-4 text-left border-r border-slate-300">NAMA SISWA</th>
                  <th className="py-2 px-3 text-center w-28 border-r border-slate-300">KELAS</th>
                  <th className="py-2 px-3 text-center w-28 border-r border-slate-300">T. ANGKATAN</th>
                  <th className="py-2 px-4 text-center w-48">VA BMI / PAYMENT</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filtered.map((st, idx) => (
                  <tr key={st.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/70'}>
                    <td className="py-1.5 px-2 text-center text-slate-600 border-r border-slate-200">{idx + 1}</td>
                    <td className="py-1.5 px-3 text-center font-mono text-slate-700 border-r border-slate-200">{st.nis}</td>
                    <td className="py-1.5 px-4 font-semibold text-slate-900 border-r border-slate-200">{st.nama}</td>
                    <td className="py-1.5 px-3 text-center text-slate-700 border-r border-slate-200">{st.kelasNama}</td>
                    <td className="py-1.5 px-3 text-center text-slate-700 border-r border-slate-200">{st.thnAngkatan}</td>
                    <td className="py-1.5 px-4 text-center font-mono font-bold text-sky-800 tracking-wider">
                      {st.noVA}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer exact from page 3 */}
          <div className="mt-8 text-xs">
            <p className="font-semibold text-slate-800">
              Ponorogo, {new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
            <p className="font-bold text-slate-900 mt-1 uppercase">SEKOLAH</p>
            <div className="h-16"></div>
            <p className="font-bold text-slate-800 border-t border-slate-300 inline-block pt-1">
              ( Kepala Tata Usaha SDMT )
            </p>
          </div>
        </div>

        {/* Bottom Toolbar (Hidden on print) */}
        <div className="bg-slate-100 border-t border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between gap-3 print:hidden">
          <span className="text-xs text-slate-500 hidden sm:inline">
            Tekan <kbd className="px-1.5 py-0.5 bg-white text-slate-700 rounded text-[11px] font-mono border border-slate-300">Esc</kbd> untuk menutup.
          </span>

          <div className="flex items-center gap-2.5 w-full sm:w-auto justify-end">
            <button
              id="btn-close-va-bottom"
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-200 hover:bg-slate-300 active:bg-slate-400 text-slate-700 font-bold rounded-lg text-xs transition flex items-center gap-1.5 cursor-pointer shadow-xs"
            >
              <X className="w-4 h-4" />
              <span>Tutup Halaman</span>
            </button>

            <button
              id="btn-print-va-bottom"
              type="button"
              onClick={() => window.print()}
              className="px-4 py-2 bg-sky-700 hover:bg-sky-600 active:bg-sky-800 text-white font-bold rounded-lg text-xs transition flex items-center gap-1.5 cursor-pointer shadow-xs"
            >
              <Printer className="w-4 h-4" />
              <span>Cetak Halaman</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
